/**
 * WebMCP Registry — core utilities for registering and unregistering tools
 * via the browser's navigator.modelContext API.
 *
 * These are plain functions (no Vue lifecycle hooks) so they can be used from
 * both Composition API composables and Options API component methods.
 */

const isInvalidStateError = (error) => error?.name === "InvalidStateError";

/**
 * Waits until navigator.modelContext is available and valid, then calls
 * the provided callback. Handles the common race condition where browser
 * extensions inject navigator.modelContext after Vue components mount.
 *
 * Returns a cancel function — call it on component unmount to stop polling.
 *
 * @param {function} callback  - called with the modelContext once available
 * @param {object}   [options]
 * @param {number}   [options.maxWaitMs=4000]   - stop polling after this many ms
 * @param {number}   [options.intervalMs=150]   - polling interval in ms
 * @returns {function} cancel — stops the polling when called
 */
export function waitForModelContext(
  callback,
  { maxWaitMs = 4000, intervalMs = 150 } = {}
) {
  const ctx = getModelContext();
  if (ctx) {
    callback(ctx);
    return () => {}; // already resolved, no cleanup needed
  }

  const deadline = Date.now() + maxWaitMs;
  const id = setInterval(() => {
    const ctx = getModelContext();
    if (ctx) {
      clearInterval(id);
      callback(ctx);
      return;
    }
    if (Date.now() >= deadline) {
      clearInterval(id);
    }
  }, intervalMs);

  return () => clearInterval(id);
}

/**
 * Returns the browser's modelContext if the WebMCP API is available,
 * or null when the environment doesn't support it.
 * Only requires registerTool — unregisterTool may not exist in all versions.
 */
export function getModelContext() {
  if (typeof navigator === "undefined") return null;

  const ctx = navigator.modelContext;
  if (!ctx) return null;

  if (typeof ctx.registerTool !== "function") {
    return null;
  }

  return ctx;
}

/**
 * Registers a tool definition with navigator.modelContext.
 * Automatically retries once if an InvalidStateError is thrown (tool already
 * registered), unregistering first and re-registering.
 *
 * @param {object} toolDefinition - WebMCP tool definition object
 * @param {object} [options]
 * @param {function} [options.onWarning] - called with (message, error) on non-fatal failures
 * @returns {boolean} true if the tool was successfully registered
 */
export function registerTool(toolDefinition, { onWarning } = {}) {
  const ctx = getModelContext();
  if (!ctx) return false;

  try {
    ctx.registerTool(toolDefinition);
    return true;
  } catch (error) {
    if (!isInvalidStateError(error)) {
      onWarning?.(
        `Failed to register WebMCP tool "${toolDefinition.name}"`,
        error
      );
      return false;
    }

    // InvalidStateError means the tool is already registered — unregister and retry
    try {
      if (typeof ctx.unregisterTool === "function") {
        ctx.unregisterTool(toolDefinition.name);
      }
      ctx.registerTool(toolDefinition);
      return true;
    } catch (retryError) {
      onWarning?.(
        `Failed to re-register WebMCP tool "${toolDefinition.name}"`,
        retryError
      );
      return false;
    }
  }
}

/**
 * Unregisters a tool from navigator.modelContext by name.
 * Silently ignores InvalidStateError (tool wasn't registered).
 * No-op if unregisterTool is not available in this version of the API.
 *
 * @param {string} toolName
 */
export function unregisterTool(toolName) {
  const ctx = getModelContext();
  if (!ctx || typeof ctx.unregisterTool !== "function") return;

  try {
    ctx.unregisterTool(toolName);
  } catch (error) {
    if (!isInvalidStateError(error)) {
      console.warn(`Failed to unregister WebMCP tool "${toolName}"`, error);
    }
  }
}
